// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, AppShell, Aside, Box, Group, Stack } from '@mantine/core';
import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { IconChevronsLeft, IconChevronsRight } from '@tabler/icons';
import { WindowContext } from '../../Service/Context/WindowContext';
import LocationPane from '../../Molecules/Event/Location/LocationPane';
import CustomerPane from '../../Organismns/Event/CustomerPane';
import EventTasksUpgrader from '../../Molecules/Event/Tasks/EventTasksUpgrader';
import EventCustomFieldsUpgrader from '../../Molecules/Event/CustomAttributes/EventCustomFieldsUpgrader';
import NotesList from '../../Molecules/Logs/EventNotes';
import Recommendations from '../../Molecules/Event/Recommendations/RecommendationsPane';
import DoubleNavbar from '../../Molecules/DoubleNavbar';
import PageHeader from '../Navigation/PageHeader';
import NavDrawer from '../../Atoms/NavDrawer';
import BookitupFooter from '../Navigation/phone/BookitupFooter';
import EventDetailsFooter from '../Navigation/phone/EventDetailsFooter';
import { MixpanelS } from '../../Service/MixpanelS';
import { ConstantS } from '../../Service/ConstantS';
import EventBreadCrumbs from './EventBreadCrumbsV2';
import EventFormNavbar from './EventFormNavBar';
import { EmailBoxContext } from '../../Service/Context/EmailBoxContext';
import EmailPageFooter from '../Navigation/phone/EmailPageFooter';

interface AsideBarContextInterface {
  isOpen: boolean;
  toggle: () => void;
}

export const AsideBarContext = createContext<AsideBarContextInterface>({} as AsideBarContextInterface);

const EventForm: FC = () => {
  const { xs, sm, md } = useContext(WindowContext);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [asideBarOpen, setAsideBarOpen] = useState<boolean>(false);
  const [asideBarCollapsed, setAsideBarCollapsed] = useState<boolean>(md);
  const { pathname } = useLocation();
  const { selectedMessage } = useContext(EmailBoxContext);
  const showFooter =
    pathname.endsWith('details') ||
    pathname.endsWith('documents') ||
    pathname.includes('emails') ||
    pathname.endsWith('shares') ||
    pathname.endsWith('resources') ||
    pathname.endsWith('finance');

  const value = useMemo(
    () => ({
      isOpen: !asideBarCollapsed,
      toggle: () => setAsideBarCollapsed(!asideBarCollapsed),
    }),
    [asideBarCollapsed],
  );

  useEffect(() => {
    if (drawerOpened && asideBarOpen) {
      setAsideBarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpened]);

  return (
    <AppShell
      header={
        <PageHeader
          subheader={
            <Box p="sm">
              <EventBreadCrumbs />
            </Box>
          }
          height={60}
        />
      }
      navbar={
        sm ? undefined : (
          <DoubleNavbar>
            <EventFormNavbar closeNavBar={() => setDrawerOpened(false)} />
          </DoubleNavbar>
        )
      }
      aside={
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          {(!sm || asideBarOpen) && (
            <Aside
              p="md"
              hiddenBreakpoint="md"
              width={{ sm: asideBarCollapsed ? 60 : 275, lg: asideBarCollapsed ? 60 : 350 }}
              sx={{ overflow: 'auto' }}
            >
              {!sm && (
                <div style={{ cursor: 'pointer' }}>
                  {asideBarCollapsed ? (
                    <ActionIcon>
                      <IconChevronsLeft onClick={() => setAsideBarCollapsed(false)} style={{ cursor: 'pointer' }} />
                    </ActionIcon>
                  ) : (
                    <Group position="right" mb={10}>
                      <ActionIcon>
                        <IconChevronsRight
                          onClick={() => {
                            setAsideBarCollapsed(true);
                            MixpanelS.track(ConstantS.TrackingEvents.EventAsideBarCollapsed);
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </ActionIcon>
                    </Group>
                  )}
                </div>
              )}
              {(!asideBarCollapsed || asideBarOpen) && (
                <Stack mb={70}>
                  <CustomerPane />
                  <Recommendations />
                  <LocationPane />
                  <EventTasksUpgrader />
                  {!pathname.endsWith('/details') && (
                    <>
                      <NotesList asideBar />
                      <EventCustomFieldsUpgrader asideBar />
                    </>
                  )}
                  {/* // TODO: Add later */}
                  {/* <EventResourcePane /> */}
                </Stack>
              )}
            </Aside>
          )}
        </>
      }
      styles={(theme) => ({
        main: {
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : 'inherit',
          width: !xs ? '100%' : undefined,
        },
      })}
      footer={
        sm && showFooter ? (
          <BookitupFooter>
            {selectedMessage ? (
              <EmailPageFooter />
            ) : (
              <EventDetailsFooter
                openNavBar={() => setDrawerOpened(true)}
                toggleAsideBar={() => setAsideBarOpen(!asideBarOpen)}
                asideBarOpen={asideBarOpen}
              />
            )}
          </BookitupFooter>
        ) : undefined
      }
    >
      <AsideBarContext.Provider value={value}>
        {sm && (
          <NavDrawer opened={drawerOpened} onClose={() => setDrawerOpened(false)}>
            <Box
              p="md"
              sx={{
                fontSize: 16,
              }}
            >
              <EventBreadCrumbs />
            </Box>
            {/* <Divider size="sm" /> */}
            <EventFormNavbar closeNavBar={() => setDrawerOpened(false)} />
          </NavDrawer>
        )}
        <Outlet />
      </AsideBarContext.Provider>
    </AppShell>
  );
};

export default EventForm;
